import ClearIcon from '@mui/icons-material/Clear'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import EditNoteIcon from '@mui/icons-material/EditNote'
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'
import CustomBackdrop from 'common/components/CustomBackdrop'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { ModalTypes } from 'common/hooks/useCustomModal'
import { DocIcon } from 'icons/DocIcon'
import i18n from 'lang/idiomas'
import { useContext, useState } from 'react'
import FilesHelper from 'src/common/libs/Files'
import { GlobalVariablesContext } from 'src/common/providers/GlobalVariablesProvider'
import DocumentViewer from 'src/features/documentos/components/DocumentViewer'
import { enrichFileForSigning, getNumeroExpediente } from './Constants'

const MIME_KIND_MAP = {
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xls',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'doc',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'ppt',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'text/plain': 'txt',
}

const kindFromFichero = (fichero) => {
  if (fichero.icono) return fichero.icono.toLowerCase().replace(/\.svg$/, '')
  return MIME_KIND_MAP[fichero.mime_type] || 'doc'
}

const DocumentDetailDialog = ({
  document: doc,
  open,
  onClose,
  onFileAction,
  statusColorMap,
  tipoDocColorMap,
}) => {
  const { handleModal } = useContext(CustomModalContext)
  const { IS_GOTTENBERG_ACTIVE } = useContext(GlobalVariablesContext)
  const [openBackdrop, setOpenBackdrop] = useState(false)

  if (!doc) return null

  const verDocumento = async (fichero) => {
    handleModal({
      enabled: true,
      closable: true,
      type: ModalTypes.Custom,
      content: (
        <DocumentViewer
          document_url={`/api/v1/documentos/descargaFichero/${fichero.token_acceso}/`}
          forced={false}
          title={fichero.nombre_archivo ?? fichero.nombre_fichero}
        />
      ),
    })
  }

  const descargar = async (fichero) => {
    setOpenBackdrop(true)
    await FilesHelper.downloadFile(
      null,
      null,
      `/api/v1/documentos/descargaFichero/${fichero.token_acceso}/`,
    ).finally(() => {
      setOpenBackdrop(false)
    })
  }

  const handleFileAction = (fichero, action) => {
    const enriched = enrichFileForSigning(fichero, doc)
    onFileAction(enriched, action)
    onClose()
  }

  const ficherosAFirmar = doc.ficheros_a_firmar ?? []
  const ficherosAsociados = doc.ficheros_asociados ?? []
  const firstDate = ficherosAFirmar[0]?.fecha_documento
  const numeroExpediente = getNumeroExpediente(doc)

  const renderFichero = (fichero, index) => {
    const colorIcon = tipoDocColorMap?.[fichero.icono]
    return (
      <ListItem
        key={fichero.token_acceso || index}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 1,
          flexWrap: 'wrap',
        }}
      >
        <ListItemAvatar>
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DocIcon kind={kindFromFichero(fichero)} color={colorIcon} size={36} decorative />
          </Box>
        </ListItemAvatar>
        <ListItemText
          primary={fichero.nombre_fichero}
          secondary={
            fichero.fecha_documento
              ? new Date(fichero.fecha_documento).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : undefined
          }
          sx={{ mr: 1 }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            ml: 'auto',
          }}
        >
          {(IS_GOTTENBERG_ACTIVE || fichero.mime_type === 'application/pdf') && (
            <Tooltip title="Ver" placement="top">
              <IconButton size="small" onClick={() => verDocumento(fichero)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Descargar" placement="top">
            <IconButton size="small" onClick={() => descargar(fichero)}>
              <CloudDownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {fichero.puede_firmar === 'S' && (
            <>
              <Tooltip title="Observaciones" placement="top">
                <IconButton size="small" onClick={() => handleFileAction(fichero, 'O')}>
                  <EditNoteIcon fontSize="small" sx={{ color: '#426bba' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Firmar" placement="top">
                <IconButton size="small" onClick={() => handleFileAction(fichero, 'F')}>
                  <FileDownloadDoneIcon fontSize="small" sx={{ color: '#7FB77E' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rechazar" placement="top">
                <IconButton size="small" onClick={() => handleFileAction(fichero, 'R')}>
                  <ClearIcon fontSize="small" sx={{ color: '#E64848' }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </ListItem>
    )
  }

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{doc.descripcion}</DialogTitle>
      <DialogContent>
        <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={statusColorMap?.[`${doc.codigo_tipo_doc}-${doc.codigo_status}`]?.label ?? doc.codigo_status}
              size="small"
              sx={
                statusColorMap?.[`${doc.codigo_tipo_doc}-${doc.codigo_status}`]
                  ? {
                      bgcolor:
                        statusColorMap[`${doc.codigo_tipo_doc}-${doc.codigo_status}`].bgcolor,
                      color: statusColorMap[`${doc.codigo_tipo_doc}-${doc.codigo_status}`].color,
                    }
                  : undefined
              }
            />
            <Typography variant="body2" color="text.secondary">
              {doc.codigo_tipo_doc}
            </Typography>
            {firstDate && (
              <Typography variant="body2" color="text.secondary">
                {new Date(firstDate).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </Typography>
            )}
            {numeroExpediente && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                {numeroExpediente}
              </Typography>
            )}
          </Box>

          {ficherosAFirmar.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {i18n.t('firmaDocumentos.archivosAFirmar', { defaultValue: 'Archivos a firmar' })}
              </Typography>
              <List>{ficherosAFirmar.map(renderFichero)}</List>
            </>
          )}

          {ficherosAsociados.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {i18n.t('firmaDocumentos.archivosAsociados', {
                  defaultValue: 'Archivos asociados',
                })}
              </Typography>
              <List>{ficherosAsociados.map(renderFichero)}</List>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
    <CustomBackdrop
      open={openBackdrop}
      text={i18n.t('descargandoArchivo', { defaultValue: 'Descargando archivo' })}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    />
  </>
  )
}

export default DocumentDetailDialog
